"use client"

import clsx from "clsx"
import * as React from "react"
import { useEffect, useState } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInputProps, splitAriaProps } from "../../utils"
import { TIcon } from "../display/TIcon"

/**
 * 텍스트 입력 컴포넌트
 *
 * @description
 * prefix, suffix, clear 버튼 등을 지원하는 텍스트 입력 필드입니다.
 * 참고: aria-*, role은 내부 input 요소에 적용됩니다(실제 접근성 트리에 이름이 노출되는 요소).
 * data-* 등 나머지 네이티브 속성은 래퍼 div에 적용됩니다.
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <TTextInput
 *   value={text}
 *   placeholder="텍스트를 입력하세요"
 *   onChange={setText}
 * />
 *
 * // prefix/suffix 사용
 * <TTextInput
 *   prefix={<SearchIcon />}
 *   suffix={<span>원</span>}
 *   value={amount}
 *   onChange={setAmount}
 * />
 * ```
 */
interface TTextInputProps extends BaseInputProps {
	/** 입력 필드 값 */
	value?: string
	/** 입력 필드 라벨 */
	label?: React.ReactNode
	/**
	 * 입력 필드 크기
	 * @default "medium"
	 * @ai-style "small" → 높이 32px
	 * @ai-style "medium" → 높이 40px
	 * @ai-style "large" → 높이 48px
	 */
	size?: "small" | "medium" | "large"
	/** 입력 필드 앞에 표시될 요소 (아이콘 등) */
	prefix?: React.ReactNode
	/** 입력 필드 뒤에 표시될 요소 (단위 등) */
	suffix?: React.ReactNode
	/** Clear 버튼 숨김 여부 */
	hiddenClear?: boolean
	/** 채워진 스타일 적용 여부 */
	filled?: boolean
	/** HTML autocomplete 속성 */
	autoComplete?: string
	/** 맞춤법 검사 활성화 여부 */
	spellCheck?: boolean
	/** 최대 입력 글자 수 — 네이티브 maxLength + 내부 slice 로 입력 자체를 차단 (THoverInput 과 동일 패턴) */
	maxLength?: number
	/** 값 변경 이벤트 핸들러 */
	onChange?: (value: string) => void
	/** 포커스 이벤트 핸들러 */
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
	/** 블러 이벤트 핸들러 */
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
	/** 키보드 이벤트 핸들러 */
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
	/** Clear 버튼 클릭 이벤트 핸들러 */
	onClear?: () => void
}

export const TTextInput = React.memo(
	React.forwardRef<HTMLInputElement, TTextInputProps>((props, ref) => {
		const {
			id,
			name,
			className,
			value,
			size = "medium",
			placeholder,
			label,
			prefix,
			suffix,
			required,
			disabled,
			error,
			readOnly,
			hiddenClear = false,
			filled,
			autoFocus,
			autoComplete = "off",
			spellCheck,
			maxLength,
			dataAttributes,
			onChange,
			onFocus,
			onBlur,
			onKeyDown,
			onClear,
			...restProps
		} = props

		const [updateValue, setUpdateValue] = useState<string>(value ?? "")
		const dataProps = useDataAttributes(dataAttributes)
		const { ariaProps, rest } = splitAriaProps(restProps)

		useEffect(() => {
			setUpdateValue(value ?? "")
		}, [value])

		const isClearVisible = !disabled && !readOnly && !hiddenClear && updateValue.length > 0

		const handleChange = React.useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const newValue = maxLength ? event.target.value.slice(0, maxLength) : event.target.value
				setUpdateValue(newValue)
				onChange?.(newValue)
			},
			[onChange, maxLength]
		)

		const handleClear = React.useCallback(
			(event: React.MouseEvent<HTMLDivElement>) => {
				event.preventDefault()
				setUpdateValue("")
				onChange?.("")
				onClear?.()
			},
			[onChange, onClear]
		)

		const handleKeyDown = React.useCallback(
			(event: React.KeyboardEvent<HTMLInputElement>) => {
				onKeyDown?.(event)
			},
			[onKeyDown]
		)

		return (
			<div className={clsx("TInput", size, className)} {...rest} data-disabled={disabled} data-readonly={readOnly} data-invalid={error} data-filled={filled} {...dataProps}>
				{label && <div className="label">{label}</div>}
				<div className="flex items-center flex-1">
					{prefix && <div className="prefix">{prefix}</div>}
					<input
						ref={ref}
						id={id}
						name={name}
						type="text"
						value={updateValue}
						placeholder={placeholder}
						required={required}
						disabled={disabled}
						readOnly={readOnly}
						autoFocus={autoFocus}
						autoComplete={autoComplete}
						spellCheck={spellCheck}
						maxLength={maxLength}
						{...ariaProps}
						aria-invalid={error}
						aria-required={required}
						onChange={handleChange}
						onFocus={onFocus}
						onBlur={onBlur}
						onKeyDown={handleKeyDown}
						{...dataProps}
					/>
					{isClearVisible && (
						<div className="clear" onClick={handleClear}>
							<TIcon name="close" size={16} />
						</div>
					)}
					{suffix && <div className="suffix">{suffix}</div>}
				</div>
			</div>
		)
	})
)

TTextInput.displayName = "TTextInput"
